import React from 'react';
import { Box, ButtonBase, type SxProps, type Theme } from '@mui/material';

export type IconPillSize = 'sm' | 'md';

/** `outlined` reads as a control; `subtle` recedes into a group of many. */
export type IconPillVariant = 'outlined' | 'subtle';

export interface IconPillProps {
  /** Leading icon node (e.g. a lucide icon). */
  icon?: React.ReactNode;
  /** Pill text. */
  label: React.ReactNode;
  /** Renders as a button when provided; otherwise a static pill. */
  onClick?: () => void;
  variant?: IconPillVariant;
  /** Filled/active treatment. */
  isSelected?: boolean;
  disabled?: boolean;
  size?: IconPillSize;
  /** Accessible label when the visible text is not descriptive enough. */
  ariaLabel?: string;
  sx?: SxProps<Theme>;
  testId?: string;
}

const SIZE_STYLES: Record<IconPillSize, { px: string; py: string; fontSize: string; gap: string }> = {
  sm: { px: '10px', py: '4px', fontSize: '12px', gap: '4px' },
  md: { px: '12px', py: '6px', fontSize: '14px', gap: '6px' },
};

const VARIANT_STYLES: Record<IconPillVariant, { border: string; background: string; hover: string }> = {
  outlined: { border: '#d9d9d9', background: '#fff', hover: '#f2f2f2' },
  subtle: { border: 'transparent', background: '#f2f3f5', hover: '#e8eaed' },
};

/**
 * IconPill - Rounded pill pairing an optional icon with a short label.
 *
 * Atomic Level: Atom
 *
 * Presentational only. Renders a `ButtonBase` when `onClick` is supplied so
 * keyboard and focus behavior come for free, and a plain `Box` otherwise.
 *
 * @example
 * ```tsx
 * <IconPill icon={<Sparkles size={14} />} label="Pool" onClick={apply} />
 * ```
 */
export function IconPill({
  icon,
  label,
  onClick,
  variant = 'outlined',
  isSelected = false,
  disabled = false,
  size = 'md',
  ariaLabel,
  sx,
  testId,
}: IconPillProps) {
  const { px, py, fontSize, gap } = SIZE_STYLES[size];
  const tone = VARIANT_STYLES[variant];

  const styles: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    gap,
    px,
    py,
    fontSize,
    lineHeight: 1.3,
    fontWeight: 500,
    borderRadius: '100px',
    border: '1px solid',
    whiteSpace: 'nowrap',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    borderColor: isSelected ? 'transparent' : tone.border,
    backgroundColor: isSelected ? '#1a1a1a' : tone.background,
    color: isSelected ? '#fff' : '#1a1a1a',
    opacity: disabled ? 0.5 : 1,
    ...(onClick && !disabled
      ? { '&:hover': { backgroundColor: isSelected ? '#1a1a1a' : tone.hover } }
      : {}),
    ...sx,
  };

  const content = (
    <>
      {icon && (
        <Box component="span" aria-hidden sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {icon}
        </Box>
      )}
      <Box component="span">{label}</Box>
    </>
  );

  if (!onClick) {
    return (
      <Box component="span" data-testid={testId} aria-label={ariaLabel} sx={styles}>
        {content}
      </Box>
    );
  }

  return (
    <ButtonBase
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={ariaLabel}
      aria-pressed={isSelected}
      sx={styles}
    >
      {content}
    </ButtonBase>
  );
}

export default IconPill;
